import React, { useMemo, useState } from 'react';
import { MailIcon, PhoneIcon, PlusIcon } from 'lucide-react';
import { ListPageLayout, type FilterChip } from '../components/lists/ListPageLayout';
import { LogActivityModal } from '../components/activity/LogActivityModal';
import { prospectStageMeta, realtorProspects } from '../data/prospects';
import { useActivityPlan } from '../contexts/ActivityPlanContext';
import { actualForPeriod, targetForPeriod } from '../utils/activityProgress';
import type { RealtorProspect } from '../types/activity';
const filters: FilterChip[] = [{
  id: 'all',
  label: 'All prospects',
  count: realtorProspects.length
}, {
  id: 'due',
  label: 'Needs a touch',
  count: realtorProspects.filter(prospect => prospect.lastTouchDaysAgo === null || prospect.lastTouchDaysAgo > 7).length
}, {
  id: 'active',
  label: 'In conversation',
  count: realtorProspects.filter(prospect => prospect.stage === 'conversation' || prospect.stage === 'meeting-set').length
}, {
  id: 'referring',
  label: 'Referring',
  count: realtorProspects.filter(prospect => prospect.stage === 'referring').length
}];
function matchesFilter(prospect: RealtorProspect, filter: string): boolean {
  if (filter === 'due') {
    return prospect.lastTouchDaysAgo === null || prospect.lastTouchDaysAgo > 7;
  }
  if (filter === 'active') {
    return prospect.stage === 'conversation' || prospect.stage === 'meeting-set';
  }
  if (filter === 'referring') return prospect.stage === 'referring';
  return true;
}
export function ProspectsPage() {
  const {
    activeGoals,
    logs
  } = useActivityPlan();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [logTarget, setLogTarget] = useState<RealtorProspect | null>(null);
  const [bulkLogOpen, setBulkLogOpen] = useState(false);
  const outreachGoal = activeGoals.find(goal => goal.category === 'realtor-outreach');
  const weekActual = actualForPeriod(logs, 'realtor-outreach', 'week');
  const weekTarget = outreachGoal ? targetForPeriod(outreachGoal, 'week') : 0;
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return realtorProspects.filter(prospect => {
      if (!matchesFilter(prospect, activeFilter)) return false;
      if (!term) return true;
      return prospect.name.toLowerCase().includes(term) || prospect.company.toLowerCase().includes(term) || prospect.market.toLowerCase().includes(term);
    });
  }, [search, activeFilter]);
  return <>
      <ListPageLayout title="Realtor Prospects" subtitle="Agents who are not referring to you yet — measured separately from your existing partners." actions={<button type="button" onClick={() => setBulkLogOpen(true)} className="inline-flex h-11 items-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-860" data-mp="2eu9ms8l0c5xc">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-861" data-mp="1zt2x39mbgpgk" />
            Log outreach
          </button>} banner={weekTarget > 0 ? <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3" data-id="element-862" data-mp="29pfo1zqh8p7m">
              <p className="text-sm font-semibold text-brand-800" data-id="element-863" data-mp="2egxpsq0bqttn">
                {weekActual} of {weekTarget} prospect touches logged this week
              </p>
              <p className="mt-0.5 text-xs text-brand-700" data-id="element-864" data-mp="2ik3rrlgelyub">
                Outreach here credits the realtor-outreach goal in your perfect
                week.
              </p>
            </div> : undefined} searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search prospects by name, brokerage, or market" filters={filters} activeFilter={activeFilter} onFilterChange={setActiveFilter} loading={false} isEmpty={visible.length === 0} emptyTitle="No prospects match" emptyMessage="Try a different filter or clear your search." resultLabel={`${visible.length} of ${realtorProspects.length} prospects`} data-id="element-859" data-mp="3q8x1f38mqwk4">
        <ul className="divide-y divide-navy-100" data-id="element-865" data-mp="32isnqv50k092">
          {visible.map(prospect => {
          const stage = prospectStageMeta[prospect.stage];
          return <li key={prospect.id} className="flex flex-wrap items-start gap-3 px-4 py-4 hover:bg-navy-50/40" data-id="element-866" data-mp="2xi508jfzu1jl">
                <div className="min-w-0 flex-1" data-id="element-867" data-mp="25u35h3ph55mk">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-868" data-mp="2jk6o7kp5ah79">
                    <p className="text-sm font-semibold text-navy-900" data-id="element-869" data-mp="2jlhqth6utiws">
                      {prospect.name}
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${stage.classes}`} data-id="element-870" data-mp="1r3m6rs484ttd">
                      {stage.label}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-navy-600" data-id="element-871" data-mp="6dgqf6ofkaur">
                    {prospect.company} · {prospect.market} ·{' '}
                    {prospect.listingsPerYear} listings/yr
                  </p>
                  <p className="mt-1 text-xs text-navy-500" data-id="element-872" data-mp="3tqb1dwsc7vdz">
                    {prospect.touches} touches ·{' '}
                    {prospect.lastTouchDaysAgo === null ? 'never contacted' : `last touch ${prospect.lastTouchDaysAgo} days ago`}{' '}
                    · source: {prospect.source}
                  </p>
                  <p className="mt-1 text-xs text-navy-600" data-id="element-873" data-mp="bcbh0usn33pf">{prospect.note}</p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-874" data-mp="g9tmucbd12t7">
                  <a href={`tel:${prospect.phone}`} aria-label={`Call ${prospect.name}`} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50" data-id="element-875" data-mp="2w20up4hlwjgj">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-876" data-mp="2czgwfnofulp5" />
                  </a>
                  <a href={`mailto:${prospect.email}`} aria-label={`Email ${prospect.name}`} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50" data-id="element-877" data-mp="2865zqfdcblay">
                    <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-878" data-mp="1t9eye0k3vzip" />
                  </a>
                  <button type="button" onClick={() => setLogTarget(prospect)} className="inline-flex h-10 items-center rounded-lg bg-navy-800 px-3 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-879" data-mp="1p9mjl644cx4w">
                    Log touch
                  </button>
                </div>
              </li>;
        })}
        </ul>
      </ListPageLayout>

      <LogActivityModal open={logTarget !== null} onClose={() => setLogTarget(null)} defaultCategory="realtor-outreach" contact={logTarget ? {
      id: logTarget.id,
      name: logTarget.name,
      detail: `${logTarget.company} · ${logTarget.market}`,
      type: 'prospect'
    } : undefined} lockContact data-id="element-880" data-mp="3m4y0x9tmgedz" />

      <LogActivityModal open={bulkLogOpen} onClose={() => setBulkLogOpen(false)} defaultCategory="realtor-outreach" data-id="element-881" data-mp="386hkgr5yj9in" />
    </>;
}